import type { LucideIcon } from 'lucide-react';

import type { AdminUserSummary } from '@/admin/adminApi';
import { accountDetailPath } from '@/admin/users/routes';

import { targetPath, type RenderedNavSection } from '../navigation';

/**
 * Modele de la palette de commandes (BO-21), **pur** : aucun composant, aucun
 * acces reseau. Ce qui se decide ici — quelle page correspond a une saisie,
 * quelle phrase dit un compte, quand une recherche part — se teste sans rendu.
 */

/**
 * Longueur minimale d'une saisie avant de chercher un compte, et attente apres
 * la derniere frappe. Chaque appel a `/admin/users?search=` ecrit une ligne
 * d'audit (ADR 0032) : sans ces deux bornes, une phrase tapee produirait une
 * ligne par lettre.
 */
export const MIN_SEARCH_LENGTH = 3;
export const SEARCH_DELAY_MS = 300;

/** Nombre de comptes proposes ; le serveur reste l'autorite sur le total. */
export const ACCOUNT_RESULT_LIMIT = 5;

/** Saisie nettoyee : c'est elle qui est comptee, comparee et envoyee. */
export const searchTermOf = (query: string): string => query.trim();

/** Une saisie assez longue pour chercher un compte (caracteres, pas unites UTF-16). */
export const isSearchable = (query: string): boolean =>
  Array.from(searchTermOf(query)).length >= MIN_SEARCH_LENGTH;

/** Minuscules, sans accents, apostrophe droite : « journal d'audit » trouve « Journal d’audit ». */
export const normalizeText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[’‘]/gu, "'")
    .toLocaleLowerCase('fr-FR')
    .trim();

// ---------------------------------------------------------------------------
// « Aller à »
// ---------------------------------------------------------------------------

export interface PalettePage {
  readonly key: string;
  readonly label: string;
  readonly sectionLabel: string;
  readonly icon: LucideIcon;
  readonly path: string;
}

/**
 * Les pages proposees sont **celles que la barre laterale rend** pour le role
 * courant (`visibleSections`) : une entree sans ecran n'y est pas, une entree
 * reservee a un role que l'on n'a pas non plus. La palette ne tient aucune liste
 * de pages a elle.
 */
export const pagesOf = (sections: readonly RenderedNavSection[]): readonly PalettePage[] =>
  sections.flatMap((section) =>
    section.entries.map((entry) => ({
      key: `page:${entry.id}`,
      label: entry.label,
      sectionLabel: section.label,
      icon: entry.icon,
      path: targetPath(entry.target),
    })),
  );

/** Chaque mot saisi doit se trouver dans le libelle ou le nom de la rubrique. */
export const filterPages = (pages: readonly PalettePage[], query: string): readonly PalettePage[] => {
  const words = normalizeText(query)
    .split(/\s+/u)
    .filter((word) => word !== '');

  if (words.length === 0) {
    return pages;
  }

  return pages.filter((page) => {
    const haystack = normalizeText(`${page.label} ${page.sectionLabel}`);

    return words.every((word) => haystack.includes(word));
  });
};

// ---------------------------------------------------------------------------
// « Comptes »
// ---------------------------------------------------------------------------

const DAY_FORMAT = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' });
const NUMBER_FORMAT = new Intl.NumberFormat('fr-FR');

/**
 * « Inscription le 12 mars 2026 » : un texte neutre, qui ne s'accorde pas (la
 * maquette ecrit « Inscrite »). Une date illisible est dite, pas inventee.
 */
export const describeRegistration = (createdAt: string): string => {
  const date = new Date(createdAt);

  return Number.isNaN(date.getTime())
    ? 'Date d’inscription inconnue'
    : `Inscription le ${DAY_FORMAT.format(date)}`;
};

export const describeProfiles = (count: number): string => {
  if (count <= 0) {
    return 'Aucun profil bébé';
  }

  return count === 1 ? '1 profil bébé' : `${NUMBER_FORMAT.format(count)} profils bébé`;
};

/** Ligne de detail d'un compte : date d'inscription et nombre de profils. */
export const describeAccount = (user: AdminUserSummary): string =>
  `${describeRegistration(user.createdAt)} · ${describeProfiles(user.babyCount)}`;

export interface AccountOption {
  readonly kind: 'account';
  readonly key: string;
  readonly path: string;
  readonly user: AdminUserSummary;
}

export interface PageOption {
  readonly kind: 'page';
  readonly key: string;
  readonly path: string;
  readonly page: PalettePage;
}

export type PaletteOption = AccountOption | PageOption;

export const accountOption = (user: AdminUserSummary): AccountOption => ({
  kind: 'account',
  key: `account:${user.id}`,
  path: accountDetailPath(user.id),
  user,
});

export const pageOption = (page: PalettePage): PageOption => ({
  kind: 'page',
  key: page.key,
  path: page.path,
  page,
});

// ---------------------------------------------------------------------------
// Etat de la recherche et annonce
// ---------------------------------------------------------------------------

export type AccountSearchState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly users: readonly AdminUserSummary[]; readonly total: number }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error'; readonly message: string };

const plural = (count: number, one: string, many: string): string =>
  `${NUMBER_FORMAT.format(count)} ${count === 1 ? one : many}`;

/**
 * Phrase lue par la region vivante : le nombre de resultats, par groupe. Un
 * groupe qui n'a pas repondu (chargement, erreur) le dit au lieu de se taire.
 */
export const announceResults = (search: AccountSearchState, pageCount: number): string => {
  const parts: string[] = [];

  switch (search.kind) {
    case 'loading':
      parts.push('recherche des comptes en cours');
      break;
    case 'error':
      parts.push('la recherche de comptes a échoué');
      break;
    case 'ready':
      parts.push(search.users.length === 0 ? 'aucun compte' : plural(search.users.length, 'compte', 'comptes'));
      break;
    case 'forbidden':
    case 'idle':
      break;
  }

  parts.push(pageCount === 0 ? 'aucune page' : plural(pageCount, 'page', 'pages'));

  return `Résultats : ${parts.join(', ')}.`;
};
